'use client';

import { useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  CircularProgress,
  InputAdornment,
  Paper,
  Popper,
  PopperProps,
  TextField,
  Typography,
} from '@mui/material';
import { Search } from 'lucide-react';
import { useRouter } from 'next/navigation';
import { InstrumentRow } from '@/components/catalogo/types';
import { useInstrumentCache } from '@/components/catalogo/ticker-cache-provider';
import { displayInstrument } from '@/libs/normalize-ticker';
import {
  findInstrumentMatch,
  rankInstrumentOptions,
  shouldNavigateInstrumentSearch,
  tickerMatchesQuery,
} from '@/libs/ticker-search';

const MAX_OPTIONS = 30;
const formatInstrumentLabel = (ticker: Pick<InstrumentRow, 'idCem' | 'fundTypeSymbol'>) =>
  displayInstrument(ticker.idCem, ticker.fundTypeSymbol);

const tickerSearchPopperSx = {
  boxSizing: 'border-box',
  width: { xs: 'min(360px, calc(100vw - 16px))', md: 420 },
  maxWidth: 'calc(100vw - 16px)',
};

const InstrumentSearchPopper = ({ style, ...props }: PopperProps) => (
  <Popper {...props} style={{ ...style, width: undefined }} sx={tickerSearchPopperSx} />
);

type InstrumentSearchProps = {
  compact?: boolean;
};

export const InstrumentSearch = ({ compact = false }: InstrumentSearchProps = {}) => {
  const router = useRouter();
  const { tickers, loading } = useInstrumentCache();
  const [inputValue, setInputValue] = useState('');
  const [open, setOpen] = useState(false);
  const [highlightedOption, setHighlightedOption] = useState<InstrumentRow | null>(null);

  const handleNavigate = (target: InstrumentRow) => {
    const label = formatInstrumentLabel(target);
    setOpen(false);
    router.push(`/ativo/${encodeURIComponent(label)}`);
  };

  const matchedOption = useMemo(() => findInstrumentMatch(tickers, inputValue), [inputValue, tickers]);

  return (
    <Autocomplete
      freeSolo
      options={tickers}
      open={open}
      onOpen={() => setOpen(true)}
      loading={loading}
      value={null}
      onChange={(_, value) => {
        setOpen(false);
        setHighlightedOption(null);
        if (value && typeof value !== 'string') {
          handleNavigate(value);
          setInputValue('');
        }
      }}
      inputValue={inputValue}
      onInputChange={(_, value) => {
        setInputValue(value);
        setOpen(Boolean(value.trim()));
        setHighlightedOption(null);
      }}
      onHighlightChange={(_, option) => setHighlightedOption(option)}
      onClose={() => {
        setOpen(false);
        setHighlightedOption(null);
      }}
      getOptionLabel={(option) =>
        typeof option === 'string' ? option : `${formatInstrumentLabel(option)} - ${option.name}`
      }
      filterOptions={(items, state) => {
        if (!state.inputValue.trim()) return items.slice(0, MAX_OPTIONS);
        return rankInstrumentOptions(
          items.filter((option) => tickerMatchesQuery(option, state.inputValue)),
          state.inputValue,
        ).slice(0, MAX_OPTIONS);
      }}
      onKeyDown={(event) => {
        if (
          event.key === 'Enter' &&
          !loading &&
          matchedOption &&
          !highlightedOption &&
          shouldNavigateInstrumentSearch(loading, matchedOption, highlightedOption)
        ) {
          event.preventDefault();
          (event as typeof event & { defaultMuiPrevented?: boolean }).defaultMuiPrevented = true;
          handleNavigate(matchedOption);
          setInputValue('');
          setOpen(false);
          setHighlightedOption(null);
        }
      }}
      renderOption={(props, option) => {
        const { key, ...optionProps } = props;
        return (
          <Box
            component='li'
            key={key}
            {...optionProps}
            sx={{
              columnGap: 1.5,
              py: 1,
              '&.MuiAutocomplete-option': {
                display: 'grid',
                gridTemplateColumns: 'minmax(0, 0.9fr) minmax(0, 1.1fr)',
                alignItems: 'start',
                minWidth: 0,
              },
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography fontWeight={700} sx={{ overflowWrap: 'anywhere', lineHeight: 1.25 }}>
                {formatInstrumentLabel(option)}
              </Typography>
              <Typography variant='caption' color='text.secondary'>
                {option.fundTypeSymbol}
              </Typography>
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant='body2'
                fontWeight={600}
                sx={{
                  fontSize: 13,
                  lineHeight: 1.3,
                  overflowWrap: 'anywhere',
                }}
              >
                {option.name}
              </Typography>
              {option.companyName ? (
                <Typography
                  variant='caption'
                  color='text.secondary'
                  sx={{
                    display: 'block',
                    fontSize: 11.5,
                    lineHeight: 1.3,
                    mt: 0.25,
                    overflowWrap: 'anywhere',
                  }}
                >
                  {option.companyName}
                </Typography>
              ) : null}
            </Box>
          </Box>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          size='small'
          placeholder='Buscar fundo'
          inputProps={{
            ...params.inputProps,
            suppressHydrationWarning: true,
          }}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <InputAdornment position='start'>
                <Search size={16} />
              </InputAdornment>
            ),
            endAdornment: (
              <>
                {loading ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      PopperComponent={InstrumentSearchPopper}
      PaperComponent={(props) => (
        <Paper
          {...props}
          elevation={6}
          sx={{
            borderRadius: 2,
            mt: 1,
            border: '1px solid hsl(var(--border))',
            boxSizing: 'border-box',
            width: '100%',
          }}
        />
      )}
      sx={{
        width: compact ? '100%' : { xs: '100%', md: 360 },
        maxWidth: '100%',
        '& .MuiInputBase-root': {
          backgroundColor: 'hsl(var(--background))',
          borderRadius: 999,
          boxShadow: '0 10px 24px hsl(var(--foreground) / 0.08)',
          ...(compact && { height: 32, minHeight: 32 }),
        },
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: 'hsl(var(--border))',
        },
        '& .MuiAutocomplete-input': {
          fontWeight: 600,
          ...(compact && { fontSize: 12.5, py: 0.25 }),
        },
        ...(compact && {
          '& .MuiInputAdornment-root svg': { width: 15, height: 15 },
        }),
      }}
    />
  );
};
